import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    api,
    type WalletProfileResponse,
    type Wallet,
    type WalletStats as WalletStatsType,
    type Trade,
    type TopMarket,
} from '@/lib/api';
import { WalletStatsCard } from '@/components/WalletStats';
import { formatDate, formatMoney, formatWalletAddress } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { ArrowLeft, ExternalLink } from 'lucide-react';

export function WalletProfile() {
    const { address } = useParams<{ address: string }>();
    const [wallet, setWallet] = useState<Wallet | null>(null);
    const [stats, setStats] = useState<WalletStatsType | null>(null);
    const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
    const [topMarkets, setTopMarkets] = useState<TopMarket[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (address) {
            fetchWallet(address);
        }
    }, [address]);

    async function fetchWallet(walletAddress: string) {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get<WalletProfileResponse>(`/api/wallets/${walletAddress}`);

            if (response.success) {
                setWallet(response.data.wallet);
                setStats(response.data.stats);
                setRecentTrades(response.data.recentTrades);
                setTopMarkets(response.data.topMarkets);
            } else {
                setError('Wallet not found');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load wallet');
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !wallet) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <span className="text-4xl mb-4">⚠️</span>
                    <p className="text-muted-foreground mb-4">{error || 'Wallet not found'}</p>
                    <Button variant="secondary" asChild>
                        <Link to="/">Back to Alerts</Link>
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const classificationConfig = {
        whale: { emoji: '🐋', label: 'Whale', variant: 'whale' as const },
        sharp: { emoji: '🎯', label: 'Sharp', variant: 'sharp' as const },
        normal: { emoji: '👤', label: 'Regular', variant: 'secondary' as const },
        unknown: { emoji: '❓', label: 'Unknown', variant: 'secondary' as const },
    };

    const config = classificationConfig[wallet.classification] || classificationConfig.unknown;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <Link
                    to="/"
                    className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Alerts
                </Link>
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold font-mono">
                        {formatWalletAddress(wallet.address)}
                    </h1>
                    <Badge variant={config.variant} className="text-sm">
                        {config.emoji} {config.label}
                    </Badge>
                </div>
                <div className="mt-2">
                    <Button variant="ghost" size="sm" className="text-muted-foreground p-0" asChild>
                        <a
                            href={`https://polymarket.com/profile/${wallet.address}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View on Polymarket
                            <ExternalLink className="w-4 h-4 ml-2" />
                        </a>
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <WalletStatsCard stats={stats} classification={wallet.classification} />

            {/* Top Markets */}
            {topMarkets.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Top Markets by Volume</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Market</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead className="text-center">Trades</TableHead>
                                    <TableHead className="text-right">Volume</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {topMarkets.map((tm, i) => (
                                    <TableRow key={tm.market.id || i}>
                                        <TableCell>
                                            <div className="max-w-[350px] truncate">
                                                {tm.market.question || tm.market.slug || tm.market.id}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-muted-foreground capitalize">
                                                {tm.market.category || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-center">{tm.trades}</TableCell>
                                        <TableCell className="text-right font-mono font-medium">
                                            {formatMoney(tm.totalVolume)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Recent Trades */}
            {recentTrades.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Recent Trades</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Time</TableHead>
                                    <TableHead>Market</TableHead>
                                    <TableHead>Side</TableHead>
                                    <TableHead>Price</TableHead>
                                    <TableHead className="text-right">Size</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {recentTrades.map((trade) => (
                                    <TableRow key={trade.id}>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {formatDate(trade.timestamp)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="max-w-[250px] truncate">
                                                {trade.marketQuestion || trade.marketSlug || trade.marketId}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={`font-semibold ${trade.side === 'YES' ? 'text-green-400' : 'text-red-400'
                                                    }`}
                                            >
                                                {trade.side}
                                            </span>
                                        </TableCell>
                                        <TableCell className="font-mono">
                                            {(trade.price * 100).toFixed(0)}¢
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-medium">
                                            {formatMoney(trade.size)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
